import {
  type QueryFunction,
  type QueryKey,
  type SetDataOptions,
  type Updater,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';

import { queryClient } from '@/resources/queryClient';
import { classifyDataQueryError } from '@/utils/queries/classifyDataQueryError';
import { type UseQueryError } from '@/types/shared';

export const useDataQuery = <
  TData,
  TError = UseQueryError<TData>,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TData, TQueryKey>,
  queryOptions?: Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>,
) => {
  const enhancedQueryOptions: Partial<UseQueryOptions<TData, TError, TData, TQueryKey>> = {
    ...(queryOptions ?? {}),
  };

  const useQueryResult = useQuery<TData, TError, TData, TQueryKey>({
    queryKey,
    queryFn,
    ...enhancedQueryOptions,
  });

  const setData = (
    updater: Updater<TData | undefined, TData>,
    options?: SetDataOptions,
  ) => {
    return queryClient.setQueryData<TData>(queryKey, updater, options);
  };

  return {
    ...useQueryResult,
    classifiedError: useQueryResult.error
      ? classifyDataQueryError(useQueryResult.error)
      : null,
    setData,
  };
};
